"use strict";

/**
* Handles the intersection events for immersive video elements, adjusting styles and video playback based on visibility.
*
* @param {number} left - The left offset value used to adjust the margin of the video block.
* @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects representing the observed elements and their intersection states.
*
* The function iterates over each entry, determining the scrolling direction and adjusting the CSS styles of the video block and its content wrapper based on the intersection ratio.
* It also manages video playback by starting or stopping the video when the intersection ratio crosses certain thresholds.
*
* - If the element is scrolling down and the intersection ratio is not decreasing, or if scrolling up and the ratio is decreasing with the element still visible, it adjusts the margin and opacity.
* - Updates the previous position and ratio attributes for each block to track changes.
* - Controls video playback by adding or removing the "is-playing" class based on the intersection ratio.
*/
function intersectionCallback(left, entries) {
    entries.forEach((entry) => {
        let $block = $(entry.target);
        let $contentWrapper = $block.find(".js-immersive-video-content-wrapper");
        let scrollingDown = $block.attr("data-prev-pos") ? $block.attr("data-prev-pos") > entry.boundingClientRect.top : true;
        let ratioGetsSmaller = ($block.attr("data-prev-ratio") ? $block.attr("data-prev-ratio") : 0) > entry.intersectionRatio;

        if ((scrollingDown && !ratioGetsSmaller) || (!scrollingDown && ratioGetsSmaller && entry.boundingClientRect.top > 0)) {
            // scrolling down, entry enters the screen from top or scrolling up, entry leaves from bottom of the screen
            $block.css("margin", `0 -${left * (entry.intersectionRatio > 0.9 ? 1 : entry.intersectionRatio)}px`);
            $contentWrapper.css("opacity", entry.intersectionRatio > 0.6 ? 1 : entry.intersectionRatio < 0.4 ? 0 : entry.intersectionRatio);
        }

        $block.attr("data-prev-pos", entry.boundingClientRect.top);
        $block.attr("data-prev-ratio", entry.intersectionRatio);
    });
}

/**
* Requests the browser to display the specified element in full-screen mode.
* This function handles compatibility across different browsers by checking
* for the appropriate full-screen request method.
*
* @param {HTMLElement} elem - The HTML element to be displayed in full-screen mode.
*/
function openInFullScreen(elem) {
    if (elem.requestFullscreen) {
        elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
    } else if (elem.webkitEnterFullscreen) { /* Safari */
        elem.webkitEnterFullscreen();
    } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
    }
}

/**
* Initializes an immersive video component by setting up an IntersectionObserver.
* The observer triggers a callback when the component's visibility changes.
*
* @param {HTMLElement} comp - The HTML element representing the immersive video component.
* The component's position is used to determine when the intersection callback should be triggered.
*
* The function creates an IntersectionObserver that observes the given component.
* The observer uses the `intersectionCallback` function to handle visibility changes,
* passing the component's left position and the intersection entries to the callback.
*/
function initializeImmersiveVideo(comp) {
    let left = comp.getBoundingClientRect().left;
    let headerHeight = $(".l-header").height() - 20;
    let observerOptions = {
        root: null,
        rootMargin: `-${headerHeight}px 0px 20px 0px`,
        threshold: Array.from({ length: 21 }, (_, i) => i / 20)
    };

    let observer = new IntersectionObserver(
        function (entries) {
            intersectionCallback(left, entries);
        },
        observerOptions
    );

    observer.observe(comp);
}

/**
* Initializes event listeners for immersive video elements to handle full-screen functionality.
* 
* This function sets up click event listeners on elements with the class 
* 'js-immersive-video-full-screen' to trigger full-screen mode for the associated video element.
* It also listens for the 'fullscreenchange' event on video elements to ensure they are muted 
* when entering full-screen mode.
* 
* @listens click - On elements with the class 'js-immersive-video-full-screen', triggers full-screen mode.
* @listens fullscreenchange - On video elements with the class 'js-immersive-video-el', mutes the video.
*/
function initImmersiveVideoEvents() {
    $("body").on("click", ".js-immersive-video-full-screen", function (e) {
        e.preventDefault();
        let videoEl = $(this).closest(".js-immersive-video").find(".js-immersive-video-el").get(0);
        openInFullScreen(videoEl);
    });

    $("body").on("fullscreenchange", ".js-immersive-video-el", function () {
        let $videoEl = $(this);

        $videoEl.prop("muted", true);
        $videoEl.trigger("play");
    });

    // safari custom event listener
    $(".js-immersive-video-el").on("webkitendfullscreen", function () {
        let $videoEl = $(this);

        setTimeout(function () {
            $videoEl.prop("muted", true);
            $videoEl.trigger("play");
        }, 1000);
    });
}

module.exports = {
    /**
    * Initializes immersive video elements on the page.
    * 
    * This function selects all elements with the class `js-immersive-video` and initializes each one using the 
    * `initializeImmersiveVideo` function. It also sets up event listeners for immersive video interactions by 
    * calling `initImmersiveVideoEvents`.
    * 
    * @function
    * @returns {void} This function does not return a value.
    */
    init: function () {
        let $immersiveVideo = $(".js-immersive-video");

        if ($immersiveVideo.length) {
            $immersiveVideo.each(function (i, el) {
                initializeImmersiveVideo(el);
            });

            initImmersiveVideoEvents();
        }
    }
};
